import {Account} from '../lib/models'
import {BsCheck, BsX} from 'react-icons/bs'
import {EN, ZH, useUserLanguage} from '../lib/translate'
import {Row, Col, Spinner} from 'react-bootstrap'
import {useFetch} from '../lib/fetch'
import Layout from '../Layout'
import {Link, useSearchParams} from 'react-router-dom'
import {useInterval} from 'react-use'
import {FaEthereum, FaCreditCard} from 'react-icons/fa'

export default function Pricing() {
  const {translateText} = useUserLanguage()
  const [account, {reload: reloadAccount}] = useFetch<Account>(['/api/accounts/self'])

  const [searchParams] = useSearchParams()
  const waitingSuccess = (!account?.subscriptionMode || account?.subscriptionMode === 'prepaid') && searchParams.get('success') === 'true'

  useInterval(reloadAccount, waitingSuccess ? 1000 : null)

  const freePlan = account?.plan === 'free'
  const paidPlan = ['pro', 'biz'].includes(account?.plan!)
  const subscribedWithPrepaid = paidPlan && account?.subscriptionMode === 'prepaid'
  const subscribedWithStripe = paidPlan && account?.subscriptionMode === 'stripe'
  const subscribedWithTokenSubs = paidPlan && account?.subscriptionMode === 'tokensubs'

  return <Layout title={translateText({en: 'Pricing', zh: '价格方案'})}>
    <Row className='justify-content-center'>
      <Col className='col col-xl-10'>
        <Row>
          <div className='w-75 p-3 pb-md-4 mx-auto text-center'>
            <h1 className='display-4 fw-normal text-body-emphasis'><EN>Pricing</EN><ZH>价格方案</ZH></h1>
          </div>
        </Row>
        <Row className='row-cols-1 row-cols-md-3 mb-3 text-center'>
          <Col>
            <div className='card mb-4 rounded-3 shadow-sm'>
              <div className='card-header py-3'>
                <h4 className='my-0 fw-normal'>Free</h4>
              </div>
              <div className='card-body'>
                <h2 className='card-title pricing-card-title'>
                  $0<small className='text-body-secondary fw-light'>/mo</small>
                </h2>
                <p className='text-body-secondary'>
                  <EN>Free for individuals new to Beancount</EN>
                  <ZH>适用于刚接触 Beancount 的个人用户</ZH>
                </p>
                <ul className='list-unstyled my-3'>
                  <li><EN>5 MB storage</EN><ZH>5 MB 存储空间</ZH></li>
                  <li><EN>1 ledger</EN><ZH>1 个账本</ZH></li>
                  <li><EN>1 collaborator</EN><ZH>1 名协作者</ZH></li>
                </ul>
              </div>
            </div>
          </Col>
          <Col>
            <div className='card mb-4 rounded-3 shadow-sm border-primary'>
              <div className='card-header py-3 border-primary'>
                <h4 className='my-0 fw-normal'>Pro</h4>
              </div>
              <div className='card-body'>
                <h2 className='card-title pricing-card-title'>
                  $5<small className='text-body-secondary fw-light'>/mo</small>
                </h2>
                <p className='text-body-secondary'>
                  <EN>For family, small business or organization</EN>
                  <ZH>适用于家庭、小型公司或组织</ZH>
                </p>
                <ul className='list-unstyled my-3'>
                  <li><EN>50 MB storage</EN><ZH>50 MB 存储空间</ZH></li>
                  <li><EN>2 ledgers</EN><ZH>2 个账本</ZH></li>
                  <li><EN>4 collaborators per ledger</EN><ZH>每个账本 4 名协作者</ZH></li>
                </ul>
                {waitingSuccess && <div className='gy-2 px-3 mt-4 d-flex justify-content-center align-items-center'>
                  <Spinner className='me-2' />
                  <span>
                    <EN>Confirming subscription...</EN>
                    <ZH>订阅结果确认中...</ZH>
                  </span>
                </div>}
                {!waitingSuccess && <Row className='gy-2 px-3'>
                  {!account && <>
                    <Link className='w-100 btn btn-lg btn-primary d-flex justify-content-center' to='/console/login'>
                      <FaEthereum className='align-self-center me-1' /> <EN>Subscribe with Crypto</EN><ZH>使用密码货币订阅</ZH>
                    </Link>
                    <Link className='w-100 btn btn-lg btn-primary d-flex justify-content-center' to='/console/login'>
                      <FaCreditCard className='align-self-center me-1' /> <EN>Subscribe with Credit Card</EN><ZH>使用信用卡订阅</ZH>
                    </Link>
                  </>}

                  {(freePlan || subscribedWithPrepaid) && <a className='btn w-100 btn-lg btn-primary d-flex justify-content-center' href={buildTokenSubsUrl(account)}>
                    <FaEthereum className='align-self-center me-1' /> <EN>Subscribe with Crypto</EN><ZH>使用密码货币订阅</ZH>
                  </a>}

                  {(freePlan || subscribedWithPrepaid) && <form className='px-0'action='/api/stripe/subscriptions' method='POST'>
                    <button className='w-100 btn btn-lg btn-primary d-flex justify-content-center' type='submit'>
                      <FaCreditCard className='align-self-center me-1' /> <EN>Subscribe with Credit Card</EN><ZH>使用信用卡订阅</ZH>
                    </button>
                  </form>}

                  {subscribedWithTokenSubs && <>
                    <a className='w-100 btn btn-lg btn-outline-secondary' target='_blank' rel='noreferrer' href='https://tokensubs.io/dashboard/subscriptions'>
                      <EN>Manage my subscription</EN><ZH>管理我的订阅</ZH>
                    </a>
                    <span className='text-muted'>
                      <EN>Subscribed with TokenSubs</EN><ZH>通过 TokenSubs 订阅</ZH>
                    </span>
                  </>}

                  {subscribedWithStripe && <>
                    <form className='px-0' target='_blank' action='/api/stripe/portal' method='POST'>
                      <button className='w-100 btn btn-lg btn-outline-secondary' type='submit'>
                        <EN>Manage my subscription</EN><ZH>管理我的订阅</ZH>
                      </button>
                    </form>
                    <span className='text-muted'>
                      <EN>Subscribed with Stripe</EN><ZH>通过 Stripe 订阅</ZH>
                    </span>
                  </>}
                </Row>}
              </div>
            </div>
          </Col>
          <Col>
            <div className='card mb-4 rounded-3 shadow-sm'>
              <div className='card-header py-3'>
                <h4 className='my-0 fw-normal'><EN>Enterprise</EN><ZH>定制版</ZH></h4>
              </div>
              <div className='card-body'>
                <p className='text-body-secondary'>
                  <EN>Contact us to customize HostedBeans features according to your needs, such as unlimited ledger size and collaborators</EN>
                  <ZH>定制 HostedBeans 的功能来适配你的需求，如无限制的存储空间和协作者数量</ZH>
                </p>
              </div>
            </div>
          </Col>
        </Row>
        <Row className='m-5'>
          <p className='lead text-center'>
            <EN>We will donate 5% of our revenue to upstream open source projects, such as Fava and Beancount.</EN>
            <ZH>我们会将收入的 5% 捐赠给上游的开源项目，比如 Fava 和 Beancount。</ZH>
          </p>
        </Row>
        <Row>
          <h2 className='display-6 text-center mb-4'><EN>Compare plans</EN><ZH>功能差异对比</ZH></h2>
          <div className='table-responsive'>
            <table className='table text-center'>
              <thead>
                <tr>
                  <th style={{width: '34%'}}></th>
                  <th style={{width: '22%'}}><EN>Free</EN><ZH>Free</ZH></th>
                  <th style={{width: '22%'}}><EN>Pro</EN><ZH>Pro</ZH></th>
                  <th style={{width: '22%'}}><EN>Enterprise</EN><ZH>定制版</ZH></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope='row' className='text-start'><EN>Private ledger</EN><ZH>私有账本</ZH></th>
                  <td><EN>Only 1</EN><ZH>1 个</ZH></td>
                  <td><EN>Up to 2</EN><ZH>最多 2 个</ZH></td>
                  <td><EN>Unlimited</EN><ZH>无限</ZH></td>
                </tr>
                <tr>
                  <th scope='row' className='text-start'><EN>Public ledger</EN><ZH>公开账本</ZH></th>
                  <td><BsX /></td>
                  <td><EN>Up to 2</EN><ZH>最多 2 个</ZH></td>
                  <td><EN>Unlimited</EN><ZH>无限</ZH></td>
                </tr>
                <tr>
                  <th scope='row' className='text-start'><EN>Ledger size</EN><ZH>账本存储空间</ZH></th>
                  <td>5 MB</td>
                  <td><EN>50 MB per ledger</EN><ZH>每个账本 50 MB</ZH></td>
                  <td><EN>Unlimited</EN><ZH>无限</ZH></td>
                </tr>
                <tr>
                  <th scope='row' className='text-start'><EN>Web UI running limit</EN><ZH>Web UI 运行时间</ZH></th>
                  <td><EN>4 hours/day</EN><ZH>每天 4 小时</ZH></td>
                  <td><EN>Unlimited</EN><ZH>无限</ZH></td>
                  <td><EN>Unlimited</EN><ZH>无限</ZH></td>
                </tr>
                <tr>
                  <th scope='row' className='text-start'><EN>Collaborators</EN><ZH>协作者</ZH></th>
                  <td><EN>Only 1</EN><ZH>1 名</ZH></td>
                  <td><EN>4 per ledger</EN><ZH>每个账本 4 名</ZH></td>
                  <td><EN>Unlimited</EN><ZH>无限</ZH></td>
                </tr>
                <tr>
                  <th scope='row' className='text-start'><EN>Readonly collaborators</EN><ZH>只读协作者</ZH></th>
                  <td><BsX /></td>
                  <td><BsCheck /></td>
                  <td><BsCheck /></td>
                </tr>
                <tr>
                  <th scope='row' className='text-start'><EN>Priority support</EN><ZH>优先技术支持</ZH></th>
                  <td><BsX /></td>
                  <td><BsCheck /></td>
                  <td><BsCheck /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Row>
      </Col>
    </Row>
  </Layout>
}

function buildTokenSubsUrl(account: Account) {
  const tokenSubsUrl = new URL('https://tokensubs.io/checkout')

  tokenSubsUrl.search = new URLSearchParams({
    merchant: process.env.TOKENSUBS_MERCHANT ?? '0x269c07a1D560F7C5bfb781c0118f5049a1e006e6',
    pricing: process.env.TOKENSUBS_PRICING ?? '5USDC/31days',
    chains: 'arbitrum,base,optimism,polygon,bsc',
    callback: 'https://www.hostedbeans.io/api/tokensubs/callback',
    redirect: 'https://www.hostedbeans.io/pricing?success=true',
    product: 'HostedBeans Pro',
    tracking: account.tokenSubsTrackingInfo,
    email: account.email,
  }).toString()

  return tokenSubsUrl.toString()
}
